import type { FeedParams, FeedResponse, Course, Challenge } from '@ai-platform/utils';
import type { PromptDto } from '../../../components/types';
import { api, ApiTags } from '../../api';
import { promptsActions } from '../../slices/prompts';
import { transformPromptDtoToModel } from '../prompt/mappers';
import { transformApiCoursesToFrontend, transformApiChallengesToFrontend } from './transformers';

type FeedTransformedData = {
    prompts: PromptDto[];
    courses: Course[];
    challenges: Challenge[];
    nextCursor: string | null;
    hasMorePrompts: boolean;
    hasMoreCourses: boolean;
    hasMoreChallenges: boolean;
};

const feedApi = api.injectEndpoints({
    endpoints: build => ({
        /**
         * Получить ленту с курсами, челленджами и промптами
         * GET /api/feed
         */
        getFeed: build.query<FeedTransformedData, FeedParams>({
            query: (params = {}) => ({
                url: '/feed',
                params: {
                    ...params,
                },
            }),
            serializeQueryArgs: ({ queryArgs }) => {
                const { hash, categories } = queryArgs;

                return {
                    hash,
                    categories: categories ? [...categories].sort().join(',') : '',
                };
            },
            transformResponse: (response: FeedResponse) => {
                const transformedPrompts = response.prompts.map(prompt => transformPromptDtoToModel(prompt));
                const courses = response.courses.map(transformApiCoursesToFrontend);
                const challenges = response.challenges.map(transformApiChallengesToFrontend);

                return {
                    prompts: transformedPrompts,
                    courses,
                    challenges,
                    nextCursor: response.next_cursor,
                    hasMorePrompts: response.has_more_prompts,
                    hasMoreCourses: response.has_more_courses,
                    hasMoreChallenges: response.has_more_challenges,
                };
            },
            onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    const { hash, categories, cursor } = params;

                    const categoriesKey = categories ? [...categories].sort().join(',') : '';
                    const cacheKey = `${hash}_${categoriesKey}`;

                    if (!cursor) {
                        dispatch(
                            promptsActions.setFeedCache({
                                key: cacheKey,
                                cache: {
                                    promptIds: data.prompts.map(p => p.id),
                                    nextCursor: data.nextCursor,
                                    hasMorePrompts: data.hasMorePrompts,
                                    hasMoreCourses: data.hasMoreCourses,
                                    hasMoreChallenges: data.hasMoreChallenges,
                                },
                                prompts: data.prompts,
                            }),
                        );

                        dispatch(promptsActions.setCourses(data.courses));
                        dispatch(promptsActions.setChallenges(data.challenges));
                    } else {
                        dispatch(
                            promptsActions.appendToFeedCache({
                                key: cacheKey,
                                promptIds: data.prompts.map(p => p.id),
                                nextCursor: data.nextCursor,
                                hasMorePrompts: data.hasMorePrompts,
                                prompts: data.prompts,
                            }),
                        );
                    }
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
            providesTags: (result, error, arg) => [
                {
                    type: ApiTags.Feed,
                    id: `${arg.hash}_${arg.categories ? [...arg.categories].sort().join(',') : ''}`,
                },
            ],
        }),
    }),
});

export const { useGetFeedQuery } = feedApi;

export { feedApi };
